import { useEffect, useMemo, useState } from "react";
import { useBottomOverlayInset } from "./use-bottom-overlay-inset.js";
import {
  AlertTriangle, ArrowDownToLine, Ban, Check, Crosshair, Dot, Ellipsis, Layers, MousePointerClick, Pencil, RotateCw,
  type LucideIcon,
} from "lucide-react";
import {
  LIVE_AI_CONNECTION_EVENT,
  LIVE_AI_DRIVE_PAUSE_EVENT,
  LIVE_AI_DRIVING_EVENT,
  isAgentFollowMode,
  isBrowserDrivePaused,
  setAgentFollowMode,
  setBrowserDrivePaused,
  type LiveAgentActivity,
  type LiveDrivingState,
} from "./live-ai-session-events.js";
import {
  useActiveAgentSessions,
  useAgentActivity,
} from "./useAgentActivity.js";
import type { AgentSessionState } from "./agent-activity-store.js";

function mediatorLabel(value?: string): string {
  if (value === "claude") return "Claude";
  if (value === "cursor") return "Cursor";
  if (value === "chatgpt") return "ChatGPT";
  if (value === "web_ui") return "In-app AI";
  if (value === "api_direct") return "API client";
  return value ? "MCP client" : "AI";
}

function sourceLabel(value?: string): string {
  if (value === "external_mcp") return "external client";
  if (value === "web_chat") return "chat";
  if (value === "plan_run_worker") return "plan run";
  if (value === "playbook_worker") return "playbook";
  if (value === "ingest_worker") return "data sync";
  return value ?? "agent";
}

function kindIcon(kind: string): LucideIcon {
  switch (kind) {
    case "tool_started": return RotateCw;
    case "tool_succeeded": return Check;
    case "tool_failed": return AlertTriangle;
    case "tool_denied": return Ban;
    case "tool_progress": return Ellipsis;
    case "data_mutation": return Pencil;
    case "browser_action": return MousePointerClick;
    case "artifact_published": return Layers;
    case "entity_focus": return Crosshair;
    case "ingest_stream": return ArrowDownToLine;
    default: return Dot;
  }
}

function kindClass(kind: string, phase?: string): string {
  if (kind === "tool_failed" || kind === "session_failed" || phase === "failed") {
    return "bg-red-50 text-red-700";
  }
  if (kind === "tool_denied") return "bg-amber-50 text-amber-700";
  if (kind === "artifact_published") return "bg-violet-50 text-violet-700";
  if (phase === "running" || kind === "tool_started") return "bg-blue-50 text-blue-700 animate-pulse";
  if (kind === "tool_succeeded" || kind === "session_completed" || phase === "succeeded") {
    return "bg-emerald-50 text-emerald-700";
  }
  return "bg-stone-50 text-stone-500";
}

function sessionStatusLabel(s: AgentSessionState): string {
  if (s.status === "completed") return "completed";
  if (s.status === "failed") return "failed";
  if (s.status === "interrupted") return "interrupted";
  if (s.status === "waiting") return "waiting";
  if (s.status === "paused") return "paused";
  if (s.current_step) return s.current_step;
  if (s.progress_current != null && s.progress_total) {
    return `step ${s.progress_current} of ${s.progress_total}`;
  }
  return "running";
}

function SessionCard({ session }: { session: AgentSessionState }) {
  const terminal = session.status === "completed" || session.status === "failed" || session.status === "interrupted";
  const pct = session.progress_total && session.progress_current != null
    ? Math.min(100, Math.round((session.progress_current / Math.max(1, session.progress_total)) * 100))
    : null;
  const tail = session.events.slice(-4);
  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${terminal ? (session.status === "completed" ? "bg-emerald-500" : "bg-red-500") : "bg-blue-500 animate-pulse"}`} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-stone-800 truncate">{session.title}</div>
          <div className="text-[10px] text-stone-400 truncate">
            {mediatorLabel(session.mediator)} · {sourceLabel(session.source)} · {sessionStatusLabel(session)}
          </div>
        </div>
        {pct != null && (
          <span className="text-[10px] font-mono text-stone-500">{pct}%</span>
        )}
      </div>
      {pct != null && (
        <div className="mt-1 h-1 rounded-full bg-stone-100 overflow-hidden">
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      {tail.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {tail.map((ev) => <ActivityRow key={ev.event_id} ev={ev} />)}
        </div>
      )}
      {session.safe_summary && (
        <div className="mt-1 text-[10px] text-stone-500 line-clamp-2">{session.safe_summary}</div>
      )}
    </div>
  );
}

function ActivityRow({ ev }: { ev: LiveAgentActivity }) {
  const KindIcon = kindIcon(ev.kind);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${kindClass(ev.kind, ev.phase)}`}>
        <KindIcon size={10} strokeWidth={2.5} aria-hidden />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-stone-700 truncate">{ev.title}</div>
        {(ev.entity_label || ev.artifact_url || ev.error_code) && (
          <div className="text-[9px] text-stone-400 truncate">
            {ev.entity_label ?? ""}
            {ev.error_code ? ` · ${ev.error_code}` : ""}
          </div>
        )}
      </div>
      {ev.artifact_url && (
        <a href={ev.artifact_url} className="text-[10px] text-blue-700 hover:underline shrink-0">open</a>
      )}
      {ev.duration_ms != null && (
        <span className="text-[9px] text-stone-400 shrink-0">{ev.duration_ms}ms</span>
      )}
    </div>
  );
}

const RAIL_EXPANDED_KEY = "cmcp:live-rail-expanded";
function readRailExpanded(): boolean {
  try { return window.localStorage.getItem(RAIL_EXPANDED_KEY) === "1"; } catch { return false; }
}
function writeRailExpanded(value: boolean) {
  try { window.localStorage.setItem(RAIL_EXPANDED_KEY, value ? "1" : "0"); } catch { /* storage unavailable */ }
}

export function LiveAISessionRail({ workspaceId }: { workspaceId?: string | null }) {
  const overlayRef = useBottomOverlayInset<HTMLElement>("rail");
  const sessions = useAgentActivity(workspaceId);
  const active = useActiveAgentSessions(workspaceId);
  // Collapsed by default and remembered: the rail is ambient status, not a
  // panel that should open itself over the page whenever an agent starts.
  const [expanded, setExpandedState] = useState(() => readRailExpanded());
  const setExpanded = (next: boolean | ((value: boolean) => boolean)) =>
    setExpandedState((value) => {
      const resolved = typeof next === "function" ? next(value) : next;
      writeRailExpanded(resolved);
      return resolved;
    });
  // Desktop: docked in the sidebar footer, clear of page content and the
  // composer. Mobile (no persistent sidebar): bottom-left, reserving space.
  const [desktop, setDesktop] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const [driving, setDriving] = useState<LiveDrivingState>({ state: "released" });
  const [connection, setConnection] = useState<"connecting" | "live" | "dropped">("connecting");
  const [paused, setPaused] = useState(isBrowserDrivePaused);
  const [follow, setFollow] = useState(isAgentFollowMode);

  useEffect(() => {
    const onDriving = (event: Event) => setDriving((event as CustomEvent<LiveDrivingState>).detail);
    const onConnection = (event: Event) => {
      const state = (event as CustomEvent<{ state: "connecting" | "live" | "dropped" }>).detail?.state;
      if (state) setConnection(state);
    };
    const onPause = (event: Event) => setPaused(!!(event as CustomEvent<{ paused: boolean }>).detail?.paused);
    const onFollow = (event: Event) => setFollow(!!(event as CustomEvent<{ on: boolean }>).detail?.on);
    window.addEventListener(LIVE_AI_DRIVING_EVENT, onDriving);
    window.addEventListener(LIVE_AI_CONNECTION_EVENT, onConnection);
    window.addEventListener(LIVE_AI_DRIVE_PAUSE_EVENT, onPause);
    window.addEventListener("cmcp:agent-follow-mode", onFollow);
    return () => {
      window.removeEventListener(LIVE_AI_DRIVING_EVENT, onDriving);
      window.removeEventListener(LIVE_AI_CONNECTION_EVENT, onConnection);
      window.removeEventListener(LIVE_AI_DRIVE_PAUSE_EVENT, onPause);
      window.removeEventListener("cmcp:agent-follow-mode", onFollow);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("cmcp-ai-driving", driving.state === "active" && !paused);
    return () => document.body.classList.remove("cmcp-ai-driving");
  }, [driving.state, paused]);

  const lead = active[0];
  const statusLabel = connection === "dropped"
    ? "Live link reconnecting"
    : paused
      ? "Browser control paused"
      : lead
        ? `${mediatorLabel(lead.mediator)} is working`
        : driving.state === "active"
          ? `${mediatorLabel(driving.mediator)} is driving this window`
          : connection === "connecting"
            ? "Connecting live workspace…"
            : "AI workspace live";

  const shown = useMemo(() => sessions.slice(0, 5), [sessions]);
  const liveHref = `/app/live${workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : ""}`;

  return (
    <section
      ref={desktop ? undefined : overlayRef}
      className={`fixed z-[70] left-3 rounded-xl pointer-events-auto ${expanded ? "w-[min(400px,calc(100vw-1.5rem))]" : desktop ? "w-[13.5rem]" : "w-auto max-w-[calc(100vw-1.5rem)]"}`}
      style={{ bottom: desktop ? "0.75rem" : "max(5rem, calc(var(--cmcp-composer-bottom-inset, 0px) + 12px))" }}
      aria-label="Live AI Session"
    >
      <div className={`rounded-xl border border-stone-200 bg-white/95 backdrop-blur-xl overflow-hidden ${expanded ? "shadow-xl max-h-[60dvh] overflow-y-auto" : "shadow-sm"}`}>
        <div className={`flex items-center gap-2 ${expanded ? "px-3 py-2.5" : "px-2.5 py-1.5"}`}>
          <button className="flex items-center gap-2 flex-1 min-w-0 text-left" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} title={expanded ? "Collapse live operations" : "Show live operations"}>
            <span className={`w-2 h-2 rounded-full shrink-0 ${connection === "dropped" ? "bg-red-500" : lead ? "bg-blue-500 animate-pulse" : connection === "connecting" ? "bg-amber-400 animate-pulse" : "bg-emerald-500"}`} />
            <span className="min-w-0">
              {expanded && <span className="block text-[10px] uppercase tracking-[0.12em] font-semibold text-stone-600">Live operations</span>}
              <span className="block text-xs font-medium text-stone-800 truncate">{statusLabel}</span>
            </span>
            {active.length > 0 && <span className="ml-auto text-[10px] font-mono text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">{active.length} active</span>}
          </button>
{expanded && (
            <>
          <button
            type="button"
            onClick={() => setAgentFollowMode(!follow)}
            className={`text-[10px] rounded-md border px-2 py-1 ${follow ? "border-violet-200 bg-violet-50 text-violet-800" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}
            title={follow ? "Stop following agent artifacts (ambient watch)" : "Follow agent — auto-open produced artifacts in this window"}
          >
            {follow ? "Following" : "Follow"}
          </button>
          <button
            type="button"
            onClick={() => setBrowserDrivePaused(!paused)}
            className={`text-[10px] rounded-md border px-2 py-1 ${paused ? "border-blue-200 bg-blue-50 text-blue-800" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}
            title={paused ? "Resume AI navigation and highlighting in this browser" : "Pause control — stop AI navigation and highlighting in this browser (live activity and data refresh continue)"}
          >
            {paused ? "Resume" : "Pause control"}
          </button>
              <button type="button" onClick={() => setExpanded(false)} className="text-stone-400 hover:text-stone-700 px-1 text-base leading-none" aria-label="Collapse live operations">×</button>
            </>
          )}
        </div>

        {expanded && (
          <div className="border-t border-stone-100">
            <div className="max-h-80 overflow-y-auto divide-y divide-stone-100">
              {shown.length === 0 && (
                <div className="px-3 py-5 text-xs text-stone-500">
                  Connected agents, chat turns, plans, playbooks, and sync runs will appear here as they work.
                </div>
              )}
              {shown.map((session) => <SessionCard key={session.session_id} session={session} />)}
            </div>
            <div className="flex items-center justify-between px-3 py-2 bg-stone-50/80 text-[10px] text-stone-500">
              <span>{paused ? "Data still refreshes; visual control is paused." : follow ? "Following — artifacts open here." : "Ambient watch — follow to auto-open artifacts."}</span>
              <a href={liveHref} className="font-medium text-blue-700 hover:underline">Live view →</a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
